import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import flags from './flags.json';

type BaseKeys = keyof typeof flags;
export type SuffixKeys = `${BaseKeys & string}_FLAG`;
export type FeatureFlagsResult = Record<SuffixKeys, boolean>;

const PREFIX = 'FF_';
const SUFFIX = '_FLAG';

const getEnvironmentFeatureFlags = (): string => {
  const buildEnvironment = (
    import.meta as ImportMeta & { env?: Record<string, string | undefined> }
  ).env;
  const runtimeEnvironment = (
    typeof window === 'undefined'
      ? undefined
      : (window as typeof window & {
          dynamikaConfig?: Record<string, string | undefined>;
        }).dynamikaConfig
  );

  return runtimeEnvironment?.FEATURE_FLAGS ?? buildEnvironment?.FEATURE_FLAGS ?? '';
};

enum SourceType {
  ENV = 0,
  LOCAL_STORAGE = 1,
}

const PRIORITY_SOURCES: [SourceType, string][] = [
  [SourceType.ENV, 'Environment Variables'],
  [SourceType.LOCAL_STORAGE, 'LocalStorage (including URL overrides)'],
];

/**
 * Get Feature Flag Value
 * It checks multiple sources in the following order:
 * 1. URL Parameters
 * 2. Local Storage
 * 3. Environment Variables
 * @param key Feature flag key
 * @param search Optional URL search string to check for overrides
 * @returns Boolean indicating if the feature flag is enabled
 */
export function getFeatureFlagValue(
  keyWithSuffix: SuffixKeys | string,
  search: string = window.location.search
): boolean {
  const rawKey = keyWithSuffix.endsWith(SUFFIX)
    ? keyWithSuffix.slice(0, -SUFFIX.length)
    : keyWithSuffix;

  const searchParams = new URLSearchParams(search);

  const urlVal = searchParams.get(rawKey) || searchParams.get(`${PREFIX}${rawKey}`);
  if (urlVal !== null) {
    localStorage.setItem(`${PREFIX}${rawKey}`, urlVal === '1' || urlVal === 'true' ? '1' : '0');
  }
  for (const [source] of PRIORITY_SOURCES) {
    if (source === SourceType.ENV) {
      const FEATURE_FLAGS = getEnvironmentFeatureFlags();
      if (FEATURE_FLAGS.split(',').includes(rawKey)) {
        return true;
      }
    }

    if (source === SourceType.LOCAL_STORAGE) {
      const lsVal = localStorage.getItem(`${PREFIX}${rawKey}`);
      if (lsVal !== null) {
        return lsVal === '1';
      }
    }
  }

  return Boolean(flags[rawKey as BaseKeys]);
}

/**
 *  Get all feature flags with their current values
 *  used mainly for React hooks
 * @param search Optional URL search string to check for overrides
 * @returns An object with all feature flags and their boolean values
 */
export const getAllFeatureFlags = (search?: string): FeatureFlagsResult => {
  return Object.keys(flags).reduce((acc, k) => {
    const key = `${k}${SUFFIX}`;
    acc[key as SuffixKeys] = getFeatureFlagValue(key as SuffixKeys, search);
    return acc;
  }, {} as FeatureFlagsResult);
};

/**
 * React hook to get all feature flags with their current values
 * It re-evaluates when the URL search string changes
 * @returns An object with all feature flags and their boolean values
 */
export function useFeatureFlag(): FeatureFlagsResult {
  const location = useLocation();

  return useMemo(() => {
    return getAllFeatureFlags(location.search);
  }, [location.search]);
}

declare global {
  interface Window {
    d7ff: Record<string, (val?: boolean) => boolean>;
  }
}

// Expose feature flag toggles for easy testing in the browser console
if (typeof window !== 'undefined') {
  window.d7ff = Object.keys(flags).reduce(
    (acc, key) => {
      acc[key] = (val?: boolean) => {
        const current = getFeatureFlagValue(key as SuffixKeys);
        const next = val !== undefined ? val : !current;
        localStorage.setItem(`${PREFIX}${key}`, next ? '1' : '0');
        console.info(`🚩 FeatureFlag [${key}] changed to: ${next}`);
        return next;
      };
      return acc;
    },
    {} as Record<string, (val?: boolean) => boolean>
  );
}
